import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, Divider, Grid } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import { subscriberTableTitles } from 'constants.js';
import { CloseIcon, EditableInput, TableWithIcons, Toolbar } from 'components';
import {
  getActiveSubscribers,
  getSuppressedSubscribers,
  removeSubscriber,
  resetSnackbar,
  suppressSubscriber,
  importFile,
  activateSubscriber,
  updateListName,
  getList,
  deleteAllSubscribers,
  getAdditionalProfileList
} from 'store/lists/actions';
import { actions as userMessageActions } from 'store/userMessaging';
import { dismissButton, dispatchButton } from 'helpers/error/buttonBuilders';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import useLists from './useLists';
import ImportFileModal from './components/ImportFileModal';
import { listHelpers } from 'store/helpers/tableAndToolbar';
import { updatePageNo } from 'store/helpers/tableAndToolbar/actions';
import AddSubscriberListModal from './components/AddSubscriberListModal';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    paddingBottom: '8px'
  },
  inputSkeleton: {
    height: '35px'
  },
  cardHeader: {
    marginTop: '16px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '5px'
  },
  closeIcon: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '0px'
  }
}));

const Subscribers = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const lists = useLists();
  const { listId } = useParams();

  const {
    validateForErrors,
    shouldShowError,
    getErrorMessage,
    resetValidationErrors,
    updateCurrentErrorValidationSegment
  } = lists;

  const {
    isTemplateReadOnly,
    isTenantDefaultTemplate,
  } = listHelpers();

  const currentList = useSelector(store => store.lists.subscribersList.currentList);
  const activeSubscribers = useSelector(store => store.lists.profileList.activeSubscribers);
  const gettingSubscribers = useSelector(store => store.lists.profileList.getting);
  const suppressedSubscribers = useSelector(
    store => store.lists.profileList.suppressedSubscribers
  );
  const isRemoveSubscriberSucceeded = useSelector(
    store => store.lists.profileList.deleted
  );
  const isActivateOrSuppressSucceeded = useSelector(store => store.lists.profileList.activatedOrSuppressed);
  const showSnackbar = useSelector(store => store.lists.showSnackbar);
  const snackbarMessage = useSelector(store => store.lists.snackbarMessage);
  const snackbarSeverity = useSelector(store => store.lists.snackbarSeverity);
  const savingInlineUpdate = useSelector(store => store.lists.subscribersList.updatingName);
  const savingInlineSucceeded = useSelector(store => store.lists.subscribersList.updatedName);

  const [filteredSubscribers, setFilteredSubscribers] = useState([]);
  const [showActiveSubscribers, setShowActiveSubscribers] = useState(true);
  const [openImportFileModal, setOpenImportFileModal] = useState(false);
  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });
  const [openAddListModal, setOpenAddListModal] = useState(false);
  const [modalType, setModalType] = useState('create');

  useEffect(() => {
    updateCurrentErrorValidationSegment('createList');
    dispatch(resetSnackbar());
    dispatch(getList(listId));
    dispatch(getActiveSubscribers(listId));
  }, []);

  useEffect(() => {
    if (isActivateOrSuppressSucceeded || isRemoveSubscriberSucceeded) {
      showActiveSubscribers
        ? dispatch(getActiveSubscribers(listId))
        : dispatch(getSuppressedSubscribers(listId));
    }
  }, [isActivateOrSuppressSucceeded, isRemoveSubscriberSucceeded]);

  useEffect(() => {
    if (showSnackbar) {
      setSnackBarInfo({
        show: true,
        message: snackbarMessage,
        severity: snackbarSeverity
      });
    } else {
      setSnackBarInfo({
        show: false,
        message: ''
      });
    }
  }, [showSnackbar]);

  useEffect(() => {
    if (showActiveSubscribers) {
      setFilteredSubscribers([...activeSubscribers]);
    } else {
      setFilteredSubscribers([...suppressedSubscribers]);
    }
  }, [activeSubscribers, suppressedSubscribers]);

  const onClickSuppressionList = () => {
    dispatch(getSuppressedSubscribers(listId));
    setShowActiveSubscribers(false);
  };

  const onClickActiveList = () => {
    dispatch(getActiveSubscribers(listId));
    setShowActiveSubscribers(true);
  };

  const onClickImport = () => {
    setOpenImportFileModal(true);
  };

  const onClickDeleteAllList = () => {
    dispatch(
      userMessageActions.setMessage({
        title: 'Delete All Subscribers',
        severity: 'warning',
        instructions: `Are you sure you want to delete all subscribers?`,
        buttons: [
          dispatchButton(
            'Delete',
            deleteAllSubscribers(listId)
          ),
          dismissButton('Cancel')
        ]
      })
    )
  }

  const refreshSubscribers = () => {
    dispatch(updatePageNo(0));
    if (showActiveSubscribers) {
      dispatch(getActiveSubscribers(listId));
    } else {
      dispatch(getSuppressedSubscribers(listId));
    }
  }

  const onClickCreate = () => {
    setModalType('create');
    setOpenAddListModal(true);
  }

  const onClickEdit = profileId => {
    dispatch(getAdditionalProfileList({ listId: listId, profileId: profileId }));
    setModalType('edit');
    setOpenAddListModal(true);
  }

  const getButtonsGroup = () => [
    ...(!isTemplateReadOnly(currentList, gettingSubscribers) && !isTenantDefaultTemplate(currentList) ? [{
      name: 'Add',
      action: () => onClickCreate()
    }] : []),
    showActiveSubscribers
      ? {
        name: 'Suppression List',
        action: () => onClickSuppressionList()
      }
      : {
        name: 'Active List',
        action: () => onClickActiveList()
      },
    ...(!isTemplateReadOnly(currentList, gettingSubscribers) ? [{
      name: 'Import',
      action: () => onClickImport()
    },
    {
      name: 'Delete All',
      disabled: filteredSubscribers.length === 0,
      action: () => onClickDeleteAllList()
    }] : []),
    {
      name: 'Refresh',
      action: () => refreshSubscribers()
    }
  ];

  const handleOnActivateOrSuppressClick = profileId => {
    if (showActiveSubscribers) {
      dispatch(
        userMessageActions.setMessage({
          title: 'Do you wish to continue?',
          severity: 'warning',
          instructions: `This subscriber can be activated anytime.`,
          buttons: [
            dispatchButton('Suppress', suppressSubscriber({ profileId: profileId, listId: listId })),
            dismissButton('Cancel')
          ]
        })
      );
    } else {
      dispatch(activateSubscriber({ profileId: profileId, listId: listId }));
    }
  };

  const handleOnRemoveClick = subscriberId => {
    dispatch(
      userMessageActions.setMessage({
        title: 'Remove Subscriber',
        severity: 'warning',
        instructions: `Are you sure you want to remove this subscriber?`,
        buttons: [
          dispatchButton(
            'Remove',
            removeSubscriber({ listId: listId, profileId: subscriberId })
          ),
          dismissButton('Cancel')
        ]
      })
    );
  };

  const getActionButtonOptions = profileId => [
    // {
    //   id: 'edit',
    //   title: 'Edit',
    //   action: () => onClickEdit(profileId)
    // },
    {
      id: showActiveSubscribers ? 'archive' : 'unarchive',
      title: showActiveSubscribers
        ? 'Suppress'
        : 'Activate',
      action: () => handleOnActivateOrSuppressClick(profileId)
    },
    {
      id: 'delete',
      title: 'Remove',
      action: () => handleOnRemoveClick(profileId)
    }
  ];

  const closeImportModal = () => {
    setOpenImportFileModal(false);
  };

  const importCsvFile = file => {
    dispatch(importFile({ listId: listId, file: file }));
  };

  const navBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/lists');
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          {isTemplateReadOnly(currentList, !currentList.name) &&
            <Alert severity="info">
              You can only view this list and do not have access to
              edit. Tenant Default list can be edited only with 'ALL' property.
            </Alert>}
        </div>
        <CloseIcon
          className={classes.closeIcon}
          onClickClose={navBack}
        />
      </div>
      <div className={classes.content}>
        <Card>
          <CardHeader
            title="List Details"
            titleTypographyProps={{ variant: 'h3' }}
          />
          <Divider />
          <CardContent>
            {currentList.name ?
              <EditableInput
                editableInputName='listName'
                label='List Name:'
                inputValue={{ id: currentList.id, value: currentList.name }}
                updateAction={(id, name) => dispatch(updateListName({ listId: id, listName: name }))}
                validateForErrors={validateForErrors}
                shouldShowError={shouldShowError}
                getErrorMessage={getErrorMessage}
                resetValidationErrors={resetValidationErrors}
                savingInlineUpdate={savingInlineUpdate}
                savingInlineSucceeded={savingInlineSucceeded}
                hideEditIcon={isTemplateReadOnly(currentList, !currentList.name)} />
              :
              <Grid item xs={4} >
                <Skeleton className={classes.inputSkeleton} />
              </Grid>
            }
          </CardContent>
        </Card>

        <Card className={classes.cardHeader}>
          <CardHeader
            title="Subscriber Profile List"
            titleTypographyProps={{ variant: 'h3' }}
            className={classes.content}
          />
          <CardContent>
            <Toolbar
              searchPlaceholder={'Search Subscribers'}
              buttonsGroup={gettingSubscribers ? [] : getButtonsGroup()}
              action={
                showActiveSubscribers
                  ? getActiveSubscribers(listId)
                  : getSuppressedSubscribers(listId)
              }
              searchInputSize='small'
            />
            <TableWithIcons
              isLoading={gettingSubscribers}
              tableTitles={subscriberTableTitles}
              actionButtonOptions={row =>
                isTemplateReadOnly(currentList, !currentList.name) ? [] : getActionButtonOptions(row.profileId)
              }
              filteredRows={filteredSubscribers}
              action={
                showActiveSubscribers
                  ? getActiveSubscribers(listId)
                  : getSuppressedSubscribers(listId)
              }
              // onDoubleClickRow={row => onClickEdit(row.profileId)}
            />
          </CardContent>
        </Card>
      </div>

      {openImportFileModal && (
        <ImportFileModal
          isOpen={openImportFileModal}
          closeImportModal={closeImportModal}
          importCsv={importCsvFile}
        />
      )}
      {openAddListModal && (
        <AddSubscriberListModal 
          isOpen={openAddListModal}
          closeCreateListModal={() => setOpenAddListModal(false)}
          listId={listId}
          type={modalType}
          onSaveSuccess={() => {
            if (showActiveSubscribers) {
              dispatch(getActiveSubscribers(listId));
            } else {
              dispatch(getSuppressedSubscribers(listId));
            }
          }}
        />
      )}
      {showSnackbar && (
        <UserMessageSnackbar
          openSnackbar={snackbarInfo.show}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
          onClose={() => dispatch(resetSnackbar())}
        />
      )}
    </div>
  );
};

export default Subscribers;
